import styled, { DefaultTheme } from 'styled-components';
import { Card, Button, Typography, Space } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(
  props: { theme?: DefaultTheme },
  darkValue: T,
  lightValue: T
): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const Container = styled.div`
  width: 100%;
  padding: 24px;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')};
  background: ${(props) => getThemeValue(props, '#1A202C', '#F7FAFC')};
  box-sizing: border-box;
  min-height: 100vh;
`;

export const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

export const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const StyledCard = styled(Card)`
  background-color: ${(props) =>
    getThemeValue(props, '#2D3748', '#ffffff')};
  border-color: ${(props) => getThemeValue(props, '#4A5568', '#E2E8F0')};
  box-shadow: ${(props) =>
    getThemeValue(
      props,
      '0 8px 24px rgba(0, 0, 0, 0.4)',
      '0 4px 12px rgba(0, 0, 0, 0.08)'
    )};
  border-radius: 12px;
  height: 100%;
  transition: all 0.3s ease;
  border: 1px solid ${(props) => getThemeValue(props, '#4A5568', '#E2E8F0')};

  &:hover {
    box-shadow: ${(props) =>
      getThemeValue(
        props,
        '0 12px 32px rgba(0, 0, 0, 0.5)',
        '0 6px 16px rgba(0, 0, 0, 0.12)'
      )};
  }

  .ant-card-head {
    background-color: ${(props) =>
      getThemeValue(props, '#2D3748', '#ffffff')};
    border-bottom-color: ${(props) =>
      getThemeValue(props, '#4A5568', '#E2E8F0')};
    color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')};
    border-radius: 12px 12px 0 0;
    padding: 20px 24px;
  }

  .ant-card-body {
    background-color: ${(props) =>
      getThemeValue(props, '#2D3748', '#ffffff')};
    color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')};
    padding: 24px;
  }

  .ant-card-head-title {
    color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')};
    font-size: 18px;
    font-weight: 600;
  }
`;

export const InfoTileCard = styled(StyledCard)`
  height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #1890ff 0%, #722ed1 100%);
  }
`;

export const InfoTileHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;

export const InfoTileContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const InfoTileTitle = styled(Typography.Title)`
  margin: 0 !important;
  font-size: 18px !important;
  font-weight: 600 !important;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
`;

export const InfoTileSubtitle = styled(Typography.Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#CBD5E0', '#718096')} !important;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const ChartContainer = styled.div`
  position: relative;
  width: 100%;
  height: 150px;
  margin-top: 16px;
`;

export const ChartValue = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
`;

export const ChartValueText = styled(Typography.Text)`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
`;

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const RankingCard = styled(StyledCard)`
  margin-bottom: 24px;
  border-radius: 12px;
  box-shadow: ${(props) =>
    getThemeValue(
      props,
      '0 8px 24px rgba(0, 0, 0, 0.4)',
      '0 4px 12px rgba(0, 0, 0, 0.08)'
    )};
  transition: all 0.3s ease;

  &:hover {
    box-shadow: ${(props) =>
      getThemeValue(
        props,
        '0 12px 32px rgba(0, 0, 0, 0.5)',
        '0 6px 16px rgba(0, 0, 0, 0.12)'
      )};
  }
`;

export const ChartSection = styled(StyledCard)`
  margin-bottom: 24px;
`;

export const ChartHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`;

export const ChartControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
`;

export const ChartMetrics = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  justify-content: center;
  align-items: center;
`;

export const MetricItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const MetricLabel = styled(Typography.Text)`
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
`;

export const MetricValue = styled(Typography.Text)`
  font-weight: 600;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
`;

export const ChartWrapper = styled.div`
  width: 100%;
  height: 380px;
  padding: 16px;
`;

export const PhoneBlurred = styled.span`
  filter: blur(3px);
  white-space: nowrap;
`;

export const OrderItemsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px;
`;

export const OrderItemCard = styled.div`
  border: 1px solid
    ${(props) => getThemeValue(props, '#4A5568', '#000000')};
  border-radius: 8px;
  padding: 16px;
  background-color: ${(props) =>
    getThemeValue(props, '#2D3748', '#ffffff')};
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')};
`;

export const OrderItemText = styled(Typography.Text)`
  display: block;
  margin-bottom: 8px;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
`;

export const StyledButton = styled(Button)`
  border-radius: 6px;
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
`;

export const SectionTitle = styled(Typography.Title)`
  margin-bottom: 20px !important;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
  font-weight: 600 !important;
  font-size: 20px !important;
`;

export const FilterSection = styled(Space)`
  width: 100%;
  margin-bottom: 16px;
`;

export const FilterLabel = styled(Typography.Text)`
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
`;
